import { Workflow as WorkflowTypes } from "./service/Workflow";
import {
    AnswerType,
    DataIngestionService,
    FieldType,
    IngestionFieldDefinition,
} from "../data-ingestion/data.ingestion.service";
import { INGESTION_POINT_TYPE_ID, INGESTION_SYSTEM_FIELDS } from "../data-ingestion/data.ingestion.const";
import { S25Assert } from "../../util/s25.assert";
import { WorkflowService } from "./service/workflow.service";
import { S25Util } from "../../util/s25-util";
import { RuleTreeService } from "../../services/rule.tree.service";
import { WorkflowSteps, WorkflowStructureValidationError } from "./workflow.process.types";

export class WorkflowUtil {
    /**
     * Populates stage names on transitions based on stage IDs
     * This is needed for validation when transitions haven't been loaded in the UI yet
     */
    static populateStageNamesOnTransitions(
        transitions: WorkflowTypes.WorkflowStageTransition[],
        stages: WorkflowTypes.WorkflowStage[],
    ): void {
        const stageIdToNameMap = new Map<number, string>();
        stages.forEach((stage) => {
            // Support both persisted stages (workflowStageId) and new stages (id)
            if (stage.workflowStageId) stageIdToNameMap.set(stage.workflowStageId, stage.stageName);
            if (stage.id) stageIdToNameMap.set(stage.id, stage.stageName);
        });
        transitions?.forEach((transition) => {
            // Always sync names from IDs to reflect any stage renames
            if (transition.fromStageId) {
                transition.fromStageName =
                    stageIdToNameMap.get(transition.fromStageId) || transition.fromStageName || "";
            }
            if (transition.toStageId) {
                transition.toStageName = stageIdToNameMap.get(transition.toStageId) || transition.toStageName || "";
            }
        });
    }

    /**
     * Private helper method for DFS cycle detection
     */
    private static detectCycleInGraph(adjacencyList: Map<string, string[]>): boolean {
        const visited = new Set<string>();
        const recursionStack = new Set<string>();

        const hasCycleDFS = (stageName: string): boolean => {
            if (recursionStack.has(stageName)) {
                return true; // Cycle detected
            }
            if (visited.has(stageName)) {
                return false; // Already processed
            }

            visited.add(stageName);
            recursionStack.add(stageName);

            const neighbors = adjacencyList.get(stageName) || [];
            for (const neighbor of neighbors) {
                if (hasCycleDFS(neighbor)) {
                    return true;
                }
            }

            recursionStack.delete(stageName);
            return false;
        };

        // Check for cycles starting from each stage
        for (const stageName of adjacencyList.keys()) {
            if (!visited.has(stageName)) {
                if (hasCycleDFS(stageName)) {
                    return true;
                }
            }
        }

        return false;
    }

    /**
     * Builds adjacency list from transitions
     */
    private static buildAdjacencyList(transitions: WorkflowTypes.WorkflowStageTransition[]): Map<string, string[]> {
        const adjacencyList = new Map<string, string[]>();

        transitions.forEach((t) => {
            if (t.fromStageName && t.toStageName) {
                if (!adjacencyList.has(t.fromStageName)) {
                    adjacencyList.set(t.fromStageName, []);
                }
                adjacencyList.get(t.fromStageName)!.push(t.toStageName);
            }
        });

        return adjacencyList;
    }

    /**
     * Detects if adding a transition would create a cycle in the workflow
     */
    static wouldCreateCycle(
        transitions: WorkflowTypes.WorkflowStageTransition[],
        newTransition: WorkflowTypes.WorkflowStageTransition,
    ): boolean {
        if (!newTransition.fromStageName || !newTransition.toStageName) {
            return false;
        }

        // Build adjacency list for all transitions including the new one
        const adjacencyList = this.buildAdjacencyList(transitions.filter((t) => t !== newTransition));

        // Add the new transition
        if (!adjacencyList.has(newTransition.fromStageName)) {
            adjacencyList.set(newTransition.fromStageName, []);
        }
        adjacencyList.get(newTransition.fromStageName)!.push(newTransition.toStageName);

        return this.detectCycleInGraph(adjacencyList);
    }

    /**
     * Detects if the entire workflow has cycles
     */
    static hasCycle(transitions: WorkflowTypes.WorkflowStageTransition[]): boolean {
        if (transitions.length === 0) {
            return false;
        }

        const adjacencyList = this.buildAdjacencyList(transitions);
        return this.detectCycleInGraph(adjacencyList);
    }

    /**
     * Validates FSM constraints: each stage can have at most one transition per type
     */
    static validateFSMConstraints(
        transitions: WorkflowTypes.WorkflowStageTransition[],
        newTransition: WorkflowTypes.WorkflowStageTransition,
    ): string[] {
        const errors: string[] = [];

        if (!newTransition.fromStageName || !newTransition.transitionType) {
            return errors;
        }

        // Check if there's already a transition of the same type from the same stage
        const existingTransition = transitions.find(
            (t) =>
                t.fromStageName === newTransition.fromStageName &&
                t.transitionType === newTransition.transitionType &&
                t !== newTransition &&
                (t.workflowStageTransitionId !== newTransition.workflowStageTransitionId || t.id !== newTransition.id),
        );

        if (existingTransition) {
            errors.push(
                `Stage "${newTransition.fromStageName}" already has a ${newTransition.transitionType.toLowerCase()} transition. ` +
                    `Each stage can only have one transition per type (approve/deny/request_changes).`,
            );
        }

        return errors;
    }

    /**
     * Gets all validation errors for a transition including FSM and cycle constraints
     */
    static getTransitionValidationErrors(
        transitions: WorkflowTypes.WorkflowStageTransition[],
        stages: WorkflowTypes.WorkflowStage[],
        transition: WorkflowTypes.WorkflowStageTransition,
    ): string[] {
        const errors: string[] = [];

        // Basic validation
        if (!transition.transitionLabel?.trim()) {
            errors.push("Transition label is required");
        } else {
            // Check for duplicate transition labels
            const isDuplicate = transitions.some(
                (t) =>
                    t.transitionLabel === transition.transitionLabel &&
                    t !== transition &&
                    (t.workflowStageTransitionId !== transition.workflowStageTransitionId || t.id !== transition.id),
            );

            if (isDuplicate) {
                errors.push(
                    `Transition label "${transition.transitionLabel}" already exists. Please choose a unique name.`,
                );
            }
        }

        if (!transition.fromStageName) {
            errors.push("From stage is required");
        }

        if (!transition.toStageName) {
            errors.push("To stage is required");
        }

        if (transition.fromStageName === transition.toStageName) {
            errors.push("From stage and to stage cannot be the same");
        }

        // Check if referenced stages exist
        if (transition.fromStageName && !stages.find((s) => s.stageName === transition.fromStageName)) {
            errors.push(`From stage "${transition.fromStageName}" does not exist`);
        }
        if (transition.toStageName && !stages.find((s) => s.stageName === transition.toStageName)) {
            errors.push(`To stage "${transition.toStageName}" does not exist`);
        }

        // Only validate transition logic if both stages exist
        const fromStage = stages.find((s: WorkflowTypes.WorkflowStage) => s.stageName === transition.fromStageName);
        const toStage = stages.find((s: WorkflowTypes.WorkflowStage) => s.stageName === transition.toStageName);

        if (fromStage && toStage) {
            if (fromStage.isTerminal) {
                errors.push("Cannot create transitions from terminal stages");
            }

            if (toStage.isStartStage) {
                errors.push("Cannot create transitions to the start stage");
            }

            // Check for cycles
            if (this.wouldCreateCycle(transitions, transition)) {
                errors.push("This transition would create a cycle in the workflow");
            }

            // Check FSM constraints
            const fsmErrors = this.validateFSMConstraints(transitions, transition);
            errors.push(...fsmErrors);
        }

        return errors;
    }

    /**
     * Generate field code from field label
     * Used by workflow components to create consistent field codes
     */
    static generateFieldCode(fieldLabel: string): string {
        if (!fieldLabel) return "";

        // Convert field name to a code-friendly format with max 32 characters
        // Backend regex only allows [A-Za-z0-9], so remove spaces entirely (no underscores)
        const code = fieldLabel
            .replace(/[^a-zA-Z0-9\s]/g, "") // Remove special characters
            .replace(/\s+/g, "") // Remove spaces entirely (no underscores)
            .toUpperCase();

        // Limit to 32 characters
        return code.length > 32 ? code.substring(0, 32) : code;
    }

    /**
     * Get field code for a field definition, generating if needed
     */
    static getFieldCode(field: IngestionFieldDefinition): string {
        // Use existing fieldCode if available, otherwise generate from fieldLabel
        if (field.fieldCode) {
            return field.fieldCode;
        }
        return WorkflowUtil.generateFieldCode(field.fieldLabel || "");
    }

    /**
     * Generate field reference string for a given field
     */
    static getFieldReference(field: IngestionFieldDefinition): string {
        const fieldCode = WorkflowUtil.getFieldCode(field);
        return `$CNET-${field.fieldDefinitionId}-${fieldCode}`;
    }

    static isWorkflowStepComplete(
        workflow: WorkflowTypes.WorkflowProcess | null | undefined,
        step: WorkflowSteps,
    ): boolean {
        if (!workflow) return false;
        switch (step) {
            case WorkflowSteps.Basic:
                return !!workflow.workflowProcessName?.trim();
            case WorkflowSteps.Stages:
                return WorkflowUtil.isStagesStepComplete(workflow);
            case WorkflowSteps.States:
                return WorkflowUtil.isStatesStepComplete(workflow);
            case WorkflowSteps.Actions:
                return WorkflowUtil.isActionsStepComplete(workflow);
            case WorkflowSteps.Transitions:
                return WorkflowUtil.isTransitionsStepComplete(workflow);
            default:
                return false;
        }
    }

    /** Matches save validation: at least one stage, one start, and one terminal. */
    static isStagesStepComplete(workflow: WorkflowTypes.WorkflowProcess): boolean {
        const stages = workflow.stages;
        if (!stages?.length) return false;
        const startStageCount = stages.filter((stage) => stage.isStartStage).length;
        const terminalStageCount = stages.filter((stage) => stage.isTerminal).length;
        return startStageCount === 1 && terminalStageCount >= 1;
    }

    /**
     * Approvals are optional (see WorkflowProcessService.validateWorkflowProcess).
     * Show complete only when the user has configured manual and/or object-level approvals.
     */
    static isStatesStepComplete(workflow: WorkflowTypes.WorkflowProcess): boolean {
        return (workflow.stages ?? []).some(
            (stage) =>
                !stage.isAccumulator &&
                !stage.isApAccumulator &&
                ((stage.states?.length ?? 0) > 0 || Boolean(stage.hasObjectApprovals)),
        );
    }

    static isActionsStepComplete(workflow: WorkflowTypes.WorkflowProcess): boolean {
        return (workflow.stages ?? []).some((stage) => (stage.actions?.length ?? 0) > 0);
    }

    static isTransitionsStepComplete(workflow: WorkflowTypes.WorkflowProcess): boolean {
        if (!WorkflowUtil.isStagesStepComplete(workflow)) return false;
        const stageCount = workflow.stages?.length ?? 0;
        if (stageCount <= 1) return true;
        return (workflow.stageTransitions?.length ?? 0) > 0;
    }

    static getStagesSummary(workflow: WorkflowTypes.WorkflowProcess | null): { id: number; name: string }[] {
        if (!workflow?.stages) return [];
        return workflow.stages.map((stage) => ({
            id: stage.workflowStageId || stage.id!,
            name: stage.stageName,
        }));
    }

    static getStatesSummary(workflow: WorkflowTypes.WorkflowProcess | null): { id: number; name: string }[] {
        if (!workflow?.stages) return [];
        const states: { id: number; name: string }[] = [];
        for (const stage of workflow.stages) {
            if (!stage.states?.length) continue;
            for (const state of stage.states) {
                states.push({
                    id: state.workflowStateId || state.id!,
                    name: state.stateName,
                });
            }
        }
        return states;
    }

    static getActionsSummary(workflow: WorkflowTypes.WorkflowProcess | null): { id: number; name: string }[] {
        if (!workflow?.stages) return [];
        const actions: { id: number; name: string }[] = [];
        for (const stage of workflow.stages) {
            if (!stage.actions?.length) continue;
            for (const action of stage.actions) {
                const name =
                    action.description?.trim() ||
                    action.actionCode
                        ?.replace(/_/g, " ")
                        .toLowerCase()
                        .replace(/\b\w/g, (l) => l.toUpperCase()) ||
                    action.actionType;
                actions.push({ id: action.workflowActionId || action.id!, name });
            }
        }
        return actions;
    }

    static getTransitionsSummary(workflow: WorkflowTypes.WorkflowProcess | null): { id: number; name: string }[] {
        if (!workflow?.stageTransitions) return [];
        return workflow.stageTransitions.map((transition) => ({
            id: transition.workflowStageTransitionId || transition.id!,
            name: transition.transitionLabel || `${transition.fromStageName} → ${transition.toStageName}`,
        }));
    }

    /** Stages that have at least one workflow-stage rule (persisted stages only; same eligibility as the Stage Rules step). */
    static async loadStagesWithWorkflowRules(
        workflow: WorkflowTypes.WorkflowProcess | null,
    ): Promise<WorkflowTypes.WorkflowStage[]> {
        if (!workflow?.workflowProcessId) return [];
        const eligible = (workflow.stages || []).filter(
            (s) => !s.isAccumulator && !s.isApAccumulator && s.workflowStageId,
        );
        if (!eligible.length) return [];
        const results = await Promise.all(
            eligible.map(async (stage) => {
                const rules = await RuleTreeService.getRulesByPrefix(
                    "workflowStage",
                    `${stage.workflowStageId}&`,
                    true,
                );
                return rules?.length ? stage : null;
            }),
        );
        return results.filter((s): s is WorkflowTypes.WorkflowStage => s !== null);
    }

    /** Approval states that have at least one workflow-state rule (persisted states only). */
    static async loadStatesWithWorkflowRules(
        workflow: WorkflowTypes.WorkflowProcess | null,
    ): Promise<WorkflowTypes.WorkflowState[]> {
        if (!workflow?.workflowProcessId) return [];
        const states: WorkflowTypes.WorkflowState[] = [];
        for (const stage of workflow.stages || []) {
            for (const state of stage.states || []) {
                if (state.workflowStateId) states.push(state);
            }
        }
        if (!states.length) return [];
        const results = await Promise.all(
            states.map(async (state) => {
                const rules = await RuleTreeService.getRulesByPrefix(
                    "workflowState",
                    `${state.workflowStateId}&`,
                    true,
                );
                return rules?.length ? state : null;
            }),
        );
        return results.filter((s): s is WorkflowTypes.WorkflowState => s !== null);
    }

    /** Actions that have at least one workflow-action rule (persisted actions only). */
    static async loadActionsWithWorkflowRules(
        workflow: WorkflowTypes.WorkflowProcess | null,
    ): Promise<WorkflowTypes.WorkflowAction[]> {
        if (!workflow?.workflowProcessId) return [];
        const actions: WorkflowTypes.WorkflowAction[] = [];
        for (const stage of workflow.stages || []) {
            for (const action of stage.actions || []) {
                if (action.workflowActionId) actions.push(action);
            }
        }
        if (!actions.length) return [];
        const results = await Promise.all(
            actions.map(async (action) => {
                const rules = await RuleTreeService.getRulesByPrefix(
                    "workflowAction",
                    `${action.workflowActionId}&`,
                    true,
                );
                return rules?.length ? action : null;
            }),
        );
        return results.filter((a): a is WorkflowTypes.WorkflowAction => a !== null);
    }

    static getStagesWithActionsPreview(workflow: WorkflowTypes.WorkflowProcess | null): { id: number; name: string }[] {
        if (!workflow?.stages) return [];
        return workflow.stages
            .filter((stage) => stage.actions?.length)
            .map((stage) => ({
                id: stage.workflowStageId || stage.id!,
                name: stage.stageName,
            }));
    }

    static answerTypeLabel(type: AnswerType): string {
        switch (type) {
            case AnswerType.STRING:
                return "Text";
            case AnswerType.LARGE_STRING:
                return "Large Text";
            case AnswerType.INTEGER:
                return "Integer";
            case AnswerType.FLOAT:
                return "Decimal";
            case AnswerType.BOOLEAN:
                return "Boolean";
            case AnswerType.DATETIME:
                return "Date/Time";
            case AnswerType.FILE:
                return "File";
            case AnswerType.JSON:
                return "JSON";
            case AnswerType.VARIANT:
                return "Variant";
            case AnswerType.IMAGE:
                return "Image";
            default:
                S25Assert.never(type, "Unknown AnswerType");
        }
    }

    static fieldTypeLabel(fieldType: FieldType): string {
        return fieldType
            .split("_")
            .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
            .join(" ");
    }
    private static readonly SYSTEM_FIELD_ANSWER_TYPES: Record<number, AnswerType> = {
        [INGESTION_SYSTEM_FIELDS.ORGANIZATION]: AnswerType.INTEGER,
        [INGESTION_SYSTEM_FIELDS.EVENT]: AnswerType.INTEGER,
        [INGESTION_SYSTEM_FIELDS.MULTISELECT_OCCURRENCES]: AnswerType.JSON,
    };

    private static readonly SYSTEM_FIELD_TYPES: Record<number, FieldType> = {
        [INGESTION_SYSTEM_FIELDS.ORGANIZATION]: "ORGANIZATION",
        [INGESTION_SYSTEM_FIELDS.EVENT]: "EVENT",
        [INGESTION_SYSTEM_FIELDS.MULTISELECT_OCCURRENCES]: "MULTISELECT_OCCURRENCES",
    };

    private static readonly FIELD_REF_REGEX = /\$CNET-(-?\d+)-\w+/g;

    static validateFieldReferences(
        inputValue: string,
        formFields: IngestionFieldDefinition[],
        acceptedAnswerTypes: AnswerType[] | undefined,
        acceptedFieldTypes?: FieldType[] | undefined,
    ): string[] {
        if (!inputValue) return [];
        const tokens = [...inputValue.matchAll(WorkflowUtil.FIELD_REF_REGEX)];
        if (!tokens.length) return [];
        if (!formFields.length) return ["Select a form context to validate field references"];
        return WorkflowUtil.validateFieldReferencesWithMap(
            tokens,
            new Map(formFields.map((f) => [f.fieldDefinitionId!, f])),
            acceptedAnswerTypes,
            acceptedFieldTypes,
        );
    }

    private static validateFieldReferencesWithMap(
        tokens: RegExpMatchArray[],
        fieldMap: Map<number, IngestionFieldDefinition>,
        acceptedAnswerTypes: AnswerType[] | undefined,
        acceptedFieldTypes?: FieldType[] | undefined,
    ): string[] {
        return tokens.flatMap(([ref, idStr]) => {
            const id = Number(idStr);
            if (id < 0) {
                const systemAnswerType = WorkflowUtil.SYSTEM_FIELD_ANSWER_TYPES[id];
                if (!systemAnswerType) return [];
                const systemFieldType = WorkflowUtil.SYSTEM_FIELD_TYPES[id];
                const answerOk = !acceptedAnswerTypes?.length || acceptedAnswerTypes.includes(systemAnswerType);
                const fieldOk =
                    !acceptedFieldTypes?.length || (!!systemFieldType && acceptedFieldTypes.includes(systemFieldType));
                if (answerOk && fieldOk) return [];
                const parts: string[] = [];
                if (!answerOk) {
                    parts.push(
                        `system field is ${WorkflowUtil.answerTypeLabel(systemAnswerType)}; accepted answer types: ${acceptedAnswerTypes!.map(WorkflowUtil.answerTypeLabel).join(", ")}`,
                    );
                }
                if (!fieldOk) {
                    const actual = systemFieldType ? WorkflowUtil.fieldTypeLabel(systemFieldType) : "(none)";
                    parts.push(
                        `field type is ${actual}; accepted: ${acceptedFieldTypes!.map(WorkflowUtil.fieldTypeLabel).join(", ")}`,
                    );
                }
                return [`${ref}: ${parts.join(" ")}`];
            }
            const field = fieldMap.get(id);
            if (!field) return [`Field reference ${ref} was not found in the selected form`];
            const answerOk =
                !acceptedAnswerTypes?.length || !field.answerType || acceptedAnswerTypes.includes(field.answerType);
            const fieldOk =
                !acceptedFieldTypes?.length ||
                (!!field.fieldType && acceptedFieldTypes.includes(field.fieldType as FieldType));
            if (answerOk && fieldOk) return [];
            const parts: string[] = [];
            if (!answerOk && field.answerType) {
                parts.push(
                    `answer type is ${WorkflowUtil.answerTypeLabel(field.answerType)}; accepted: ${acceptedAnswerTypes!.map(WorkflowUtil.answerTypeLabel).join(", ")}`,
                );
            }
            if (!fieldOk) {
                const actual =
                    field.fieldType != null ? WorkflowUtil.fieldTypeLabel(field.fieldType as FieldType) : "(none)";
                parts.push(
                    `field type is ${actual}; accepted: ${acceptedFieldTypes!.map(WorkflowUtil.fieldTypeLabel).join(", ")}`,
                );
            }
            return [`"${field.fieldLabel ?? ref}": ${parts.join(". ")}`];
        });
    }

    /**
     * Validates all field references in a workflow before saving.
     * Loads field definitions per action's form context (cached by ingestion point ID).
     * Returns a flat list of error strings; empty means the workflow is safe to save.
     */
    static async validateWorkflowBeforeSave(
        workflow: WorkflowTypes.WorkflowProcess,
        cache: Map<number, IngestionFieldDefinition[]> = new Map(),
    ): Promise<string[]> {
        const errors: string[] = [];
        const actions = workflow.stages?.flatMap((s) => (s.actions ?? []).map((a) => ({ stage: s, action: a }))) ?? [];

        // Prefetch all distinct form field definitions in parallel
        const ingestionPointIds = [
            ...new Set(
                actions
                    .map(({ action: a }) =>
                        Number(a.actionItemTypeId) === INGESTION_POINT_TYPE_ID ? Number(a.actionItemId) : undefined,
                    )
                    .filter((id): id is number => !!id && !cache.has(id)),
            ),
        ];
        await Promise.all(
            ingestionPointIds.map(async (id) => {
                const [fields, err] = await S25Util.Maybe(DataIngestionService.getFieldDefinitions(id));
                cache.set(id, err ? [] : (fields ?? []));
            }),
        );

        for (const { stage, action } of actions) {
            const ingestionPointId =
                Number(action.actionItemTypeId) === INGESTION_POINT_TYPE_ID ? Number(action.actionItemId) : undefined;
            const fields = ingestionPointId ? (cache.get(ingestionPointId) ?? []) : [];
            const fieldMap = new Map(fields.map((f) => [f.fieldDefinitionId!, f]));
            const actionLabel = action.description?.trim() || action.actionCode || "Action";
            const prefix = `${stage.stageName} / ${actionLabel}`;

            if (action.isSystemAction && action.actionCode) {
                const meta =
                    WorkflowService.SYSTEM_ACTIONS[action.actionCode as keyof typeof WorkflowService.SYSTEM_ACTIONS];
                const inputMeta = meta && "inputs" in meta ? (meta.inputs ?? []) : [];
                for (const inputEntry of action.inputs ?? []) {
                    if (!inputEntry.inputValue) continue;
                    const tokens = [...inputEntry.inputValue.matchAll(WorkflowUtil.FIELD_REF_REGEX)];
                    if (!tokens.length) continue;
                    if (!fields.length) {
                        errors.push(
                            `${prefix} (${inputEntry.inputCode}): Select a form context to validate field references`,
                        );
                        continue;
                    }
                    const inputDef = inputMeta.find((m) => m.inputCode === inputEntry.inputCode) as
                        | WorkflowTypes.SystemActionInput
                        | undefined;
                    const errs = WorkflowUtil.validateFieldReferencesWithMap(
                        tokens,
                        fieldMap,
                        inputDef?.acceptedAnswerTypes,
                        inputDef?.acceptedFieldTypes,
                    );
                    errors.push(...errs.map((e) => `${prefix} (${inputEntry.inputCode}): ${e}`));
                }
            }

            for (const value of [action.jsonPayloadTemplate, action.httpUrl, action.httpHeaders]) {
                if (!value) continue;
                const tokens = [...value.matchAll(WorkflowUtil.FIELD_REF_REGEX)];
                if (!tokens.length) continue;
                if (!fields.length) {
                    errors.push(`${prefix}: Select a form context to validate field references`);
                    break;
                }
                const errs = WorkflowUtil.validateFieldReferencesWithMap(tokens, fieldMap, undefined, undefined);
                errors.push(...errs.map((e) => `${prefix}: ${e}`));
            }

            if (action.isSystemAction && action.actionCode === "ASSIGN_LIVE_25_DRAFT_EVENT") {
                if (!ingestionPointId) {
                    errors.push(
                        `${prefix}: Assign 25Live Draft Event requires a form that includes a 25Live Event Form field`,
                    );
                } else {
                    const hasLive25EventFormField = fields.some(
                        (fieldDef) => fieldDef.fieldType === "LIVE_25_EVENT_FORM" && !fieldDef.isDeleted,
                    );
                    if (!hasLive25EventFormField) {
                        errors.push(
                            `${prefix}: Assign 25Live Draft Event requires the form to include a 25Live Event Form field`,
                        );
                    }
                }
            }
        }

        return errors;
    }

    /**
     * Validates workflow stage graph, accumulators, approvals, and spawn actions before save.
     * Returns the first validation failure, or null when the structure is valid.
     */
    static validateWorkflowStructure(workflow: WorkflowTypes.WorkflowProcess): WorkflowStructureValidationError | null {
        const fail = (message: string, step: WorkflowSteps, indices?: number[]): WorkflowStructureValidationError => ({
            message,
            step,
            indices,
        });

        const stages = workflow.stages || [];
        const stageTransitions = workflow.stageTransitions || [];

        WorkflowUtil.populateStageNamesOnTransitions(stageTransitions, stages);

        if (stages.length === 0) {
            return fail("At least one stage is required", WorkflowSteps.Stages);
        }

        const stageNames = new Set<string>();
        const duplicateStageIndices: number[] = [];
        stages.forEach((stage, stageIndex) => {
            const trimmedName = stage.stageName?.trim();
            if (trimmedName) {
                if (stageNames.has(trimmedName)) {
                    duplicateStageIndices.push(stageIndex);
                } else {
                    stageNames.add(trimmedName);
                }
            }
        });
        if (duplicateStageIndices.length) {
            return fail("Stage names must be unique", WorkflowSteps.States, duplicateStageIndices);
        }

        const transitionLabels = new Set<string>();
        const duplicateTransitionIndices: number[] = [];
        stageTransitions.forEach((transition, transitionIndex) => {
            const trimmedLabel = transition.transitionLabel?.trim();
            if (trimmedLabel) {
                if (transitionLabels.has(trimmedLabel)) {
                    duplicateTransitionIndices.push(transitionIndex);
                } else {
                    transitionLabels.add(trimmedLabel);
                }
            }
        });
        if (duplicateTransitionIndices.length) {
            return fail(
                "<p>Stage transition labels must be unique</p>",
                WorkflowSteps.Transitions,
                duplicateTransitionIndices,
            );
        }

        const startStages = stages.filter((stage) => stage.isStartStage);
        if (startStages.length !== 1) {
            const message =
                startStages.length === 0
                    ? "Workflow must have exactly one start stage"
                    : "Workflow can only have one start stage";
            return fail(message, WorkflowSteps.Stages);
        }

        const terminalStages = stages.filter((stage) => stage.isTerminal);
        if (terminalStages.length === 0) {
            return fail("Workflow must have at least one terminal stage", WorkflowSteps.Stages);
        }

        if (stages.length > 1) {
            const reachableStages = new Set<string>();
            const startStage = stages.find((stage) => stage.isStartStage);
            if (startStage) {
                reachableStages.add(startStage.stageName);
            }

            let changed = true;
            while (changed) {
                changed = false;
                stageTransitions.forEach((transition) => {
                    if (reachableStages.has(transition.fromStageName) && !reachableStages.has(transition.toStageName)) {
                        reachableStages.add(transition.toStageName);
                        changed = true;
                    }
                });
            }

            const unreachableStages = stages.filter(
                (stage) => !reachableStages.has(stage.stageName) && !stage.isStartStage,
            );
            if (unreachableStages.length) {
                const message = `<p>The following stages are unreachable: <b>${unreachableStages.map((stage) => stage.stageName).join(", ")}</b>. Please ensure all stages are connected via transitions.</p>`;
                return fail(message, WorkflowSteps.Transitions);
            }
        }

        const stagesWithOutgoing = new Set(stageTransitions.map((transition) => transition.fromStageName));
        const stagesWithNoOutgoing =
            stages
                ?.filter((stage) => !stage.isTerminal && !stagesWithOutgoing.has(stage.stageName))
                ?.map((stage) => stage.stageName) ?? [];
        if (stagesWithNoOutgoing.length) {
            const message = `<p>The following stages have no outgoing transitions: <b>${stagesWithNoOutgoing.join(", ")}</b>. If these stages are terminal, please update their type in Step 2.</p>`;
            return fail(message, WorkflowSteps.Transitions);
        }

        const terminalWithOutgoing =
            stages
                ?.filter((stage) => stage.isTerminal && stagesWithOutgoing.has(stage.stageName))
                ?.map((stage) => stage.stageName) ?? [];
        if (terminalWithOutgoing.length) {
            const message = `<p>The following terminal stages have outgoing transitions: <b>${terminalWithOutgoing.join(", ")}</b>. Terminal stages cannot have outgoing transitions.</p>`;
            return fail(message, WorkflowSteps.Transitions);
        }

        if (WorkflowUtil.hasCycle(stageTransitions)) {
            return fail(
                "Workflow contains cycles. Stages must form a directed acyclic graph (DAG).",
                WorkflowSteps.Stages,
            );
        }

        const fsmErrors = new Set(
            stageTransitions.flatMap((transition) => WorkflowUtil.validateFSMConstraints(stageTransitions, transition)),
        );
        if (fsmErrors.size) {
            const message = [...fsmErrors].map((error) => `<p>${error}</p>`).join("");
            return fail(message, WorkflowSteps.Transitions);
        }

        for (const stage of stages) {
            if (stage.states?.length) {
                const stateNames = new Set<string>();
                const duplicateNames = new Set<string>();
                stage.states.forEach((state) => {
                    const trimmedName = state.stateName?.trim();
                    if (trimmedName) {
                        if (stateNames.has(trimmedName)) {
                            duplicateNames.add(trimmedName);
                        } else {
                            stateNames.add(trimmedName);
                        }
                    }
                });

                if (duplicateNames.size) {
                    const message = `<p>The following approvals in <b>${stage.stageName}</b> contain duplicate names: <b>${[...duplicateNames].join(", ")}</b>. Approval names must be unique within a stage.</p>`;
                    return fail(message, WorkflowSteps.States);
                }
            }

            // Accumulators are synchronization gates that wait for spawned child workflows to complete.
            if (stage.isAccumulator && stage.isApAccumulator) {
                return fail(
                    `Stage "${stage.stageName}" cannot be both an Accumulator and an AP Accumulator.`,
                    WorkflowSteps.Stages,
                );
            }

            if (stage.isAccumulator || stage.isApAccumulator) {
                const accumulatorLabel = stage.isApAccumulator ? "AP Accumulator" : "Accumulator";
                if (stage.isStartStage) {
                    return fail(
                        `${accumulatorLabel} stage "${stage.stageName}" cannot be a start stage.`,
                        WorkflowSteps.Stages,
                    );
                }
                if (stage.isTerminal) {
                    return fail(
                        `${accumulatorLabel} stage "${stage.stageName}" cannot be a terminal stage.`,
                        WorkflowSteps.Stages,
                    );
                }
                if (stage.actions?.length) {
                    return fail(
                        `${accumulatorLabel} stage "${stage.stageName}" cannot have actions.`,
                        WorkflowSteps.Actions,
                    );
                }
                if (stage.states?.length) {
                    return fail(
                        `${accumulatorLabel} stage "${stage.stageName}" cannot have approvals.`,
                        WorkflowSteps.States,
                    );
                }
                if (stage.hasObjectApprovals) {
                    return fail(
                        `${accumulatorLabel} stage "${stage.stageName}" cannot have object approvals.`,
                        WorkflowSteps.Stages,
                    );
                }
            }

            if (stage.hasObjectApprovals && WorkflowUtil.stageActionUsesAssignmentPolicy(stage)) {
                return fail(
                    `Stage "${stage.stageName}" cannot use both Object Approvals and Assignment Policy.`,
                    WorkflowSteps.Stages,
                );
            }

            if (stage.actions?.length) {
                for (const [actionIndex, action] of stage.actions.entries()) {
                    if (action.actionCode !== "SPAWN_WORKFLOW") {
                        continue;
                    }
                    if (stage.isTerminal) {
                        return fail(
                            `Stage "${stage.stageName}" has a spawn form action but is marked as terminal. Stages that spawn forms cannot be terminal.`,
                            WorkflowSteps.Stages,
                            [actionIndex],
                        );
                    }
                    if (stage.isStartStage) {
                        return fail(
                            `Stage "${stage.stageName}" has a spawn form action but is marked as start. Stages that spawn forms cannot be start stages.`,
                            WorkflowSteps.Actions,
                            [actionIndex],
                        );
                    }
                    if (!action.spawnActionHandling) {
                        return fail(
                            `Please select an Action Handling Strategy for ${stage.stageName}`,
                            WorkflowSteps.Actions,
                            [actionIndex],
                        );
                    }

                    if (action.spawnReuseSameItem) {
                        if (!action.spawnWorkflowProcessId) {
                            return fail(
                                `Please select a Workflow to Spawn for ${stage.stageName}. A workflow process is required to reuse form submission.`,
                                WorkflowSteps.Actions,
                                [actionIndex],
                            );
                        }
                    } else if (action.spawnIteratorFieldDefinitionId != null) {
                        if (!action.dispatchMappings?.length) {
                            return fail(
                                `Please add at least one dispatch mapping for the iterator action on ${stage.stageName}.`,
                                WorkflowSteps.Actions,
                                [actionIndex],
                            );
                        }
                        const invalidMapping = action.dispatchMappings.find(
                            (mapping) => !mapping.targetIngestionPointId || !mapping.dispatchValue,
                        );
                        if (invalidMapping) {
                            return fail(
                                `Each dispatch mapping on ${stage.stageName} must have a value and a target form.`,
                                WorkflowSteps.Actions,
                                [actionIndex],
                            );
                        }
                    } else {
                        if (!action.spawnChildParentItemId) {
                            return fail(
                                `Please select a Child Form Context for ${stage.stageName}. A form context for the child workflow submission is required.`,
                                WorkflowSteps.Actions,
                                [actionIndex],
                            );
                        }
                        if (!action.spawnChildParentItemTypeId) {
                            return fail(
                                `Please select an Item Type for ${stage.stageName}. A form context for the child workflow submission is required.`,
                                WorkflowSteps.Actions,
                                [actionIndex],
                            );
                        }
                    }
                }
            }
        }

        return null;
    }

    static isTruthyWorkflowCheckboxValue(value: string | undefined): boolean {
        return value != null && /^\s*(1|true)\s*$/i.test(value);
    }

    static stageActionUsesAssignmentPolicy(stage: WorkflowTypes.WorkflowStage): boolean {
        if (!stage.actions?.length) {
            return false;
        }
        return stage.actions.some((action) =>
            action.inputs?.some(
                (actionInput) =>
                    actionInput.inputCode === "useAssignmentPolicy" &&
                    WorkflowUtil.isTruthyWorkflowCheckboxValue(actionInput.inputValue),
            ),
        );
    }
}
